/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
  DateTimePicker as MuiDateTimePicker,
  LocalizationProvider
} from '@material-ui/pickers';
import MomentUtils from '@material-ui/pickers/adapter/moment';
import {Moment} from 'moment';
import {WhitelistedLocales} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import TextField from '../textfield/TextField';

export interface DateTimePickerProps {
  value: Moment;
  onChange: (date: Moment) => void;
  error?: boolean;
  locale?: WhitelistedLocales;
  readOnly?: boolean;
  fieldLabel?: string;
  textFieldOverrides?: (theme: DLSTheme) => GenericObject;
  PopperProps?: GenericObject;
  DialogProps?: GenericObject;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: Moment;
  maxDate?: Moment;
  inputFormat?: string;
  allowKeyboardControl?: boolean;
  errorText?: string;
  enableClock?: boolean;
  calenderViews?: Array<any>;
  readOnlyText?: boolean;
  overrideStyles?: GenericObject;
  placeholder?: string;
}

const getStyle = (className: string, property: string, fallbackStyle: string) =>
  ({overrideStyles}: {overrideStyles: GenericObject}) => overrideStyles?.[className]?.[property] || fallbackStyle
;

const useStyles = makeStyles(theme => ({
  dateTimePicker : {
    '& div.MuiPickersToolbar-root' : {
      display : 'none'
    },
    '& div.MuiPickersCalendarHeader-monthTitleContainer h6' : {
      fontSize : getStyle('div.MuiPickersCalendarHeader-monthTitleContainer h6', 'fontSize', '28px')
    }
  },
  errorText : {
    color : dlsColor('caRed')
  }
}));

/**
 * @DateTimePicker
 *
 * DateTimePicker that utilizes material ui's v4 date time picker input
 * with a popper that displays teh calendar then clock for time selection
 * after user chooses a date.
 *
 * ```typescript
 *
 * import {DateTimePicker} from '@ion/dls/src/components/date';
 *
 * <DateTimePicker value={momentValue}
 *   onChange={onChange}
 *   locale='fr'
 *   fieldLabel='Choose date & time' />
 *
 * ```
 *
 * @param {Moment} value: value to be rendered in the picker's input
 * @param {Function} onChange: fired when the date or time is changed
 * @param {boolean} error: if there is an error it is provided to this component
 * @param {string} locale: determines the language and formatting of the calendar and time
 * @param {boolean} readOnly: if true then the input is disabled
 * @param {string} fieldLabel: text field label
 * @param {Function<object>} textFieldOverrides: returns the styles that get merged
 *        with the base styles for the text field
 * @param {object} PopperProps: props that get passed to the date/time popper
 * @param {object} DialogProps: props that get passed to the date/time dialog
 * @param {boolean} disablePast: disables past dates on the calendar
 * @param {boolean} disableFuture: disables future dates on the calendar
 * @param {Moment} minDate: minimum date to be available on the calendar. Meaning any
 *        dates further back than this date will not be available
 * @param {Moment} maxDate: maximum date to be available on the calendar. Meaning any
 *        dates after this date will not be available
 *
 */
export const DateTimePicker = ({
  value,
  onChange,
  error,
  readOnly,
  textFieldOverrides,
  PopperProps,
  DialogProps,
  minDate,
  maxDate,
  inputFormat = 'M/D/YYYY h:mm A',
  fieldLabel = '',
  disablePast = false,
  disableFuture = false,
  allowKeyboardControl = true,
  enableClock = true,
  readOnlyText = false,
  errorText = '',
  calenderViews = ['year', 'month', 'date', 'hours', 'minutes'],
  locale = WhitelistedLocales.en,
  overrideStyles = {},
  placeholder = null
}: DateTimePickerProps) => {
  const classes = useStyles({overrideStyles});

  return (
    <LocalizationProvider dateAdapter={MomentUtils} locale={locale}>
      <MuiDateTimePicker clearable
        ampm={enableClock}
        allowKeyboardControl={allowKeyboardControl}
        className={classes.dateTimePicker}
        inputFormat={inputFormat}
        disabled={readOnly}
        views={calenderViews}
        renderInput={function(props) {
          return (
            <TextField hasError={error}
              readOnlyText={readOnlyText}
              styleOverrides={textFieldOverrides}
              fieldLabel={fieldLabel}
              placeholder={placeholder || inputFormat} {...props}
              helperText={
                <span className={classes.errorText}>{errorText || error}</span>
              }
              errorText={
                <span className={classes.errorText}>{error}</span>
              } />
          );
        }}
        value={value}
        onChange={onChange}
        PopperProps={PopperProps}
        DialogProps={DialogProps}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate} />
    </LocalizationProvider>
  );
};

export default DateTimePicker;
